// src/ui/VolumeControl.jsx — the speaker button, and the slider that grows out
// of it.
//
// 🟢 THE ICON IS THE MUTE BUTTON AND THE CONTAINER IS THE LEVEL. One click
//    silences, one click brings it back — that is the gesture people already
//    have. The slider is not a second click deeper: hovering the button opens
//    it, so lowering the volume is a move and a drag, never a menu.
//
// 🟥 IT MUST NOT PASS ITS CLICKS ON. The control sits on top of a video whose
//    own click opens the reel — without stopPropagation, turning the sound down
//    would also navigate away from the thing you were listening to.
//
// The track is a div, not <input type="range">, because a range input cannot be
// styled consistently across browsers without more CSS than the control itself,
// and this one has to sit on video without looking like a form.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeVolume, volumeState, setLevelFor, toggleMuted,
} from '../services/volume';

/* ── the speaker, three states ───────────────────────────────────────────────
   🟢 DEFINED HERE, NOT IN icons.jsx, for once. Nothing else in the app draws a
      speaker, and keeping them next to the only thing that uses them means this
      control is one file with no second module to be out of step with — which
      is exactly how the first version of it broke.

   They share the same cone path on purpose, so the glyph does not jump when the
   level crosses a threshold: only the waves to its right change. */
const SV = (p) => ({
  width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...p,
});
const CONE = 'M11 5.2 6.6 8.9H3.4v6.2h3.2L11 18.8Z';
const VolumeX = (p) => (
  <svg {...SV(p)}><path d={CONE} />
    <line x1="15.4" y1="9.6" x2="20.6" y2="14.4" /><line x1="20.6" y1="9.6" x2="15.4" y2="14.4" />
  </svg>
);
const Volume1 = (p) => (
  <svg {...SV(p)}><path d={CONE} /><path d="M15.4 9.8a3.4 3.4 0 0 1 0 4.4" /></svg>
);
const Volume2 = (p) => (
  <svg {...SV(p)}><path d={CONE} />
    <path d="M15.4 9.8a3.4 3.4 0 0 1 0 4.4" /><path d="M18.2 7.2a7 7 0 0 1 0 9.6" />
  </svg>
);

/** Live volume state for one surface. */
export function useVolume(scope) {
  const [v, setV] = useState(volumeState);
  useEffect(() => subscribeVolume(setV), []);
  return {
    level: v.level,
    muted: !!v[scope],
    /* what a player should multiply the author's mix by */
    scale: v[scope] ? 0 : v.level,
  };
}

export default function VolumeControl({ scope, className = '', place = 'right' }) {
  const { level, muted } = useVolume(scope);
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);

  const shown = open || dragging;
  const pct = Math.round((muted ? 0 : level) * 100);

  const fromEvent = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width <= 0) return;
    setLevelFor(scope, (clientX - r.left) / r.width);
  }, [scope]);

  const startDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    fromEvent(e.clientX);
    const move = (ev) => fromEvent(ev.clientX);
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const nudge = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    e.stopPropagation();
    setLevelFor(scope, level + (e.key === 'ArrowRight' ? 0.05 : -0.05));
  };

  const Icon = muted || pct === 0 ? VolumeX : pct < 50 ? Volume1 : Volume2;

  return (
    <div
      className={`vb-vol ${place} ${shown ? 'open' : ''} ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        className="vb-vol-btn"
        onClick={(e) => { e.stopPropagation(); toggleMuted(scope); }}
        aria-label={muted ? 'Unmute' : 'Mute'}
        title={muted ? 'Unmute' : 'Mute'}
      >
        <Icon width={17} height={17} />
      </button>
      <div className="vb-vol-slot">
        <div
          ref={trackRef}
          className="vb-vol-track"
          role="slider"
          tabIndex={0}
          aria-label="Volume"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          onPointerDown={startDrag}
          onKeyDown={nudge}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
        >
          <span className="vb-vol-fill" style={{ width: `${pct}%` }} />
          <span className="vb-vol-knob" style={{ left: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}
